import React, { useState } from 'react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '@snipit/core/services/firebase';
import { Monitor, Loader2, CheckCircle } from 'lucide-react';

export default function DesktopAuthView() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleDesktopAuth = async () => {
    setLoading(true);
    setError('');
    
    try {
      const provider = new GoogleAuthProvider();
      // Force Google account selection
      provider.setCustomParameters({ prompt: 'select_account' });
      
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      
      if (credential && credential.idToken) {
        setSuccess(true);
        // Retrieve the state parameter from the URL to pass back for CSRF protection
        const urlParams = new URLSearchParams(window.location.search);
        const stateParam = urlParams.get('state');
        
        let redirectUrl = `snipit://auth?token=${credential.idToken}`;
        if (stateParam) {
          redirectUrl += `&state=${encodeURIComponent(stateParam)}`;
        }
        
        // Redirect back to the Tauri desktop app via deep link
        window.location.href = redirectUrl;
      } else {
        setError('Failed to retrieve authentication token from Google.');
      }
    } catch (err) {
      console.error('Desktop Auth Error:', err);
      setError(err.message || 'An error occurred during authentication.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-gray-800 p-8 rounded-2xl shadow-2xl max-w-md w-full flex flex-col items-center text-center">
        <Monitor className="w-16 h-16 text-indigo-500 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">
          Authenticate Desktop App
        </h1>
        
        {!success ? (
          <>
            <p className="text-gray-400 mb-8 text-sm">
              You are securely authenticating the Snip-IT Desktop application. Click below to confirm your Google account.
            </p>
            
            {error && (
              <div className="w-full bg-red-900/30 border border-red-900/60 text-red-400 p-3 rounded mb-6 text-sm">
                {error}
              </div>
            )}
            
            <button 
              onClick={handleDesktopAuth}
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Authorize Snip-IT Desktop'}
            </button>
          </>
        ) : (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mb-4 mt-4" />
            <p className="text-gray-300 mb-4">
              Authentication successful!
            </p>
            <p className="text-gray-500 text-sm">
              You can now close this tab and return to the Snip-IT Desktop app.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
